import { type FC, useMemo } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";

import {
  Flex,
  SortHeader,
  IconBadge,
  Image,
  DashPlaceholder,
  Text,
  IAddPropertyModalProps,
  PropertiesTable,
} from "dash-ui";
import {
  DATE_FORMAT,
  ProjectStatusesEnum,
  PropertyStatusesEnum,
} from "dash-common";

import { RoutesPath } from "flow-components/routes/routes";
import usePropertiesData from "common/hooks/usePropertiesData";
import { BadgePropertyStatusChange } from "containers/properties/components/BadgePropertyStatusChange";
import {
  GetPropertiesDocument,
  GetPropertyByIdQuery,
  useCreatePropertyMutation,
  useGetOrganizationsForSelectQuery,
} from "gql/generated.graphql";

import type { ICMSPropertiesTableProps } from "./CMSPropertiesTable.interfaces";

const NameColumnFlex = styled(Flex)`
  cursor: pointer;
`;

const StyledIconBadge = styled(IconBadge)`
  :hover {
    background-color: ${({ theme }) => theme.colors.g20};
    border: 1px solid ${({ theme }) => theme.colors.g20};
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const CMSPropertiesTable: FC<ICMSPropertiesTableProps> = ({
  hideColumns,
  actionProps,
  ...tableProps
}) => {
  const { t } = useTranslation("tables", {
    keyPrefix: "properties",
  });
  const navigate = useNavigate();
  const { t: tLabels } = useTranslation("common", { keyPrefix: "labels" });

  const { data, loading: loadingProperties } = usePropertiesData();

  const [createProperty, { loading: loadingCreateProperty }] =
    useCreatePropertyMutation({
      refetchQueries: [GetPropertiesDocument],
    });

  const handleCreateProperty: IAddPropertyModalProps["handleCreateProperty"] = (
    formValues,
    onCompleted,
    onError
  ) => {
    const {
      name,
      primaryOrganizationId,
      market,
      city,
      county,
      state,
      street,
      zipCode,
      place,
    } = formValues;

    createProperty({
      variables: {
        input: {
          name,
          marketAreaId: market,
          primaryOrganizationId,
          location: {
            city,
            county,
            state,
            street,
            zipCode,
            googlePlaceId: place?.place_id,
            lat: place?.geometry?.location.lat(),
            lng: place?.geometry?.location.lng(),
          },
        },
      },
      onCompleted: (data) => {
        navigate(`${RoutesPath.Properties}/${data.createProperty._id}`);
        onCompleted();
      },
      onError,
    });
  };
  const { data: organizationsData, loading: loadingOrganizations } =
    useGetOrganizationsForSelectQuery({
      variables: { pagination: { allowLimit: false } },
    });

  // FIXME: re-check this memo
  const columns = useMemo(
    () =>
      [
        {
          Header: () => <SortHeader accessor="name">{t`nameLabel`}</SortHeader>,
          accessor: "name",
          name: "name",
          Cell: ({
            value,
            row,
          }: {
            value: string;
            row: { original: GetPropertyByIdQuery["getPropertyById"] };
          }) => {
            const file = row.original.mainPropertyMediaFile?.files[0];

            return (
              <NameColumnFlex
                alignItems="center"
                onClick={() =>
                  navigate(
                    RoutesPath.PropertyDetails.replace(":id", row.original._id)
                  )
                }
              >
                {file ? (
                  <Image
                    mr={2}
                    src={file.thumbnailFile?.publicUrl || ""}
                    fit="cover"
                  />
                ) : (
                  <DashPlaceholder mr={2} />
                )}
                {value}
              </NameColumnFlex>
            );
          },
        },
        {
          Header: () => (
            <SortHeader accessor="organization.name">{tLabels`organization`}</SortHeader>
          ),
          accessor: "primaryOrganization.name",
          name: "primaryOrganization",
        },
        {
          Header: () => (
            <SortHeader accessor="updatedAt">{t`updatedAtLabel`}</SortHeader>
          ),
          accessor: "updatedAt",
          Cell: ({ value }: { value: Date }) =>
            moment(value).format(DATE_FORMAT),
          name: "updatedAt",
        },
        {
          Header: () => (
            <SortHeader accessor="projectStatus">{tLabels`propertyStatus`}</SortHeader>
          ),
          accessor: "projectStatus",
          name: "projectStatus",
          Cell: ({ value }: { value: ProjectStatusesEnum }) => {
            const propertyStatus = value ? tLabels(value.toLowerCase()) : "N/A";
            return propertyStatus;
          },
        },
        {
          Header: () => (
            <SortHeader accessor="status">{tLabels`status`}</SortHeader>
          ),
          accessor: "status",
          name: "status",
          Cell: ({
            value,
            row: {
              original: { _id, primaryOrganizationId, marketArea, location },
            },
          }: {
            value: PropertyStatusesEnum;
            row: {
              original: {
                _id: string;
                primaryOrganizationId: string;
                marketArea: any;
                location: any;
              };
            };
          }) => (
            <BadgePropertyStatusChange
              currentStatus={value}
              propertyId={_id}
              primaryOrganizationId={primaryOrganizationId}
              marketArea={marketArea}
              location={location}
            />
          ),
        },
        {
          Header: () => (
            <SortHeader accessor="note">{tLabels`notes`}</SortHeader>
          ),
          accessor: "note",
          name: "note",
          Cell: ({ row }: { row: { original: { _id: string } } }) => (
            <Link
              target="_self"
              to={`${RoutesPath.Properties}/${row.original._id}?tab=NOTES`}
            >
              <StyledIconBadge small icon="note" variation="primary" />
            </Link>
          ),
        },
        {
          Header: () => <Text whiteSpace="nowrap">{tLabels`preview`}</Text>,
          accessor: "_id",
          Cell: ({ row }: { row: { original: { _id: string } } }) => (
            <Link
              to={RoutesPath.PropertyPreview.replace(":id", row.original._id)}
              target="_blank"
            >
              <StyledIconBadge small icon="show" variation="primary" />
            </Link>
          ),
          name: "preview",
        },
      ].filter(({ name }) => !hideColumns?.includes(name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tLabels, hideColumns]
  );

  return (
    <PropertiesTable
      actionProps={{
        ...actionProps,
        handleCreateProperty,
        organizations: organizationsData?.getOrganizations.collection || [],
        loadingCreateProperty,
        loadingOrganizations,
      }}
      columns={columns}
      data={data}
      loading={loadingProperties}
      {...tableProps}
    />
  );
};

export default CMSPropertiesTable;
